<template>
  <div>
    <!-- Toolbar -->
    <div class="toolbar">
      <button @click="exec('bold')"><b>B</b></button>
      <button @click="exec('italic')"><i>I</i></button>
      <button @click="exec('underline')"><u>U</u></button>

    <button v-if="!isLinkActive" @click="insertLink">Link</button>
    <button v-if="isLinkActive" @click="exec('unlink')">Unlink</button>

    <select @change="setColor($event)">
    <option disabled selected>Color</option>
    <option value="">Default</option> <!-- New option -->
    <option value="red">Red</option>
    <option value="blue">Blue</option>
    <option value="green">Green</option>
    <option value="orange">Orange</option>
    </select>

      <button @click="undo" :disabled="!canUndo">↩️</button>
      <button @click="redo" :disabled="!canRedo">↪️</button>
    </div>

    <!-- Editor -->
    <div
      ref="editor"
      class="editor"
      contenteditable="true"
      @input="handleInput"
      @keydown="handleKeydown"
    ></div>
  </div>
</template>

<script>
import { ref, computed, defineComponent, onMounted, onBeforeUnmount, watch } from 'vue'

export default defineComponent({
  name: 'WysiwygEditor',
  props: {
    modelValue: String,
    name: String
  },
  emits: ['update:modelValue', 'default'],
  setup(props, { emit }) {
    const editor = ref(null)
    const STORAGE_KEY = 'wysiwyg-autosave'
    const isLinkActive = ref(false)

    const history = ref([])
    const historyIndex = ref(0)

    const canUndo = computed(() => historyIndex.value > 0)
    const canRedo = computed(() => historyIndex.value < history.value.length - 1)

    const pushToHistory = () => {
      const html = editor.value.innerHTML
      if (html !== history.value[historyIndex.value]) {
        history.value = history.value.slice(0, historyIndex.value + 1)
        history.value.push(html)
        if (history.value.length > 20) history.value.shift()
        else historyIndex.value++
      }
    }

    const updateContent = () => {
      emit('update:modelValue', editor.value.innerHTML)
    }

    const exec = (command, value = null) => {
      document.execCommand(command, false, value)
      handleInput()
    }

    const insertLink = () => {
      let url = prompt('Enter link URL:')
      if (!url) return
      if (!/^https?:\/\//i.test(url)) url = 'http://' + url
      document.execCommand('createLink', false, url)
      handleInput()
    }

    const setColor = (event) => {
        const color = event.target.value
        if (color === '') {
            document.execCommand('removeFormat', false, null)
        } else {
            document.execCommand('foreColor', false, color)
        }
        event.target.selectedIndex = 0
        handleInput()
    }

    const undo = () => {
      if (canUndo.value) {
        historyIndex.value--
        editor.value.innerHTML = history.value[historyIndex.value]
        updateContent()
      }
    }

    const redo = () => {
      if (canRedo.value) {
        historyIndex.value++
        editor.value.innerHTML = history.value[historyIndex.value]
        updateContent()
      }
    }

    const handleKeydown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault()
        undo()
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault()
        redo()
      }
    }

    const handleInput = () => {
      autoLinkify()
      enforceLinkAttributes()
      pushToHistory()
      updateContent()
    }

    const autoLinkify = () => {
      const urlRegex = /(https?:\/\/[^\s<]+)/g
      editor.value.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && urlRegex.test(node.textContent)) {
          const newHtml = node.textContent.replace(urlRegex, (url) => {
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
          })
          const span = document.createElement('span')
          span.innerHTML = newHtml
          editor.value.replaceChild(span, node)
        }
      })
    }

    const enforceLinkAttributes = () => {
      const links = editor.value.querySelectorAll('a')
      links.forEach((link) => {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      })
    }

    const checkIfLinkSelected = () => {
      const sel = window.getSelection()
      if (!sel.rangeCount) {
        isLinkActive.value = false
        return
      }
      const anchor = sel.anchorNode?.parentElement
      isLinkActive.value = anchor?.tagName === 'A'
    }

    const selectionListener = () => checkIfLinkSelected()

    onMounted(() => {
      const saved = localStorage.getItem(STORAGE_KEY)
      const initial = props.modelValue || saved || ''
      editor.value.innerHTML = initial
      history.value = [initial]
      historyIndex.value = 0
      if (!props.modelValue && saved) emit('default', initial, props.name)

      document.addEventListener('selectionchange', selectionListener)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('selectionchange', selectionListener)
      clearInterval(interval)
    })

    const interval = setInterval(() => {
      if (editor.value) {
        localStorage.setItem(STORAGE_KEY, editor.value.innerHTML)
      }
    }, 5000)

    watch(() => props.modelValue, (val) => {
      if (val !== editor.value.innerHTML) {
        editor.value.innerHTML = val
      }
    })

    return {
      editor,
      exec,
      insertLink,
      setColor,
      undo,
      redo,
      canUndo,
      canRedo,
      handleKeydown,
      handleInput,
      isLinkActive
    }
  }
})
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--input-bg);
  padding: 0 10px;
  border-radius: 10px 10px 0 0;
}
.toolbar button {
  padding: 5px 10px;
  background: transparent;
  border: 0;
  cursor: pointer;
      color: var(--black);
    box-shadow: none;
}
.toolbar select {
  background: var(--input-bg);
  border: 0;
}
.editor {
  min-height: 150px;
  padding: 10px;
  outline: none;
  border: 2px solid var(--input-bg);
  border-top: none;
  border-radius: 0 0 10px 10px;
  color: var(--black);
}
</style>
